import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import { ThreeDots } from "react-loader-spinner";
import moment from "moment";
import theme from "../../theme";
import history from "../../history";
import { hasCap } from "../../lib/permissions";
import {
  getNotificationCountAction,
  getNotificationsAction,
  markNotificationReadAction,
  markAllNotificationsReadAction,
} from "../../containers/dashboard/action";

const COUNT_POLL_MS = 45000;
const NOTIF_PAGE_SIZE = 10;

const useStyles = makeStyles(() => ({
  panel: {
    width: "380px",
    maxWidth: "94vw",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
  },
  title: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    fontSize: "16px",
  },
  markAll: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    fontSize: "12.5px",
    padding: "4px 6px",
  },
  markAllDisabled: { color: theme.colors.Violet, opacity: 0.4, cursor: "default" },
  list: { maxHeight: "420px", overflowY: "auto" },
  item: {
    display: "flex",
    gap: "10px",
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: theme.colors.Porcelain },
  },
  itemUnread: { backgroundColor: "#F3F8FF" },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    marginTop: "6px",
    flexShrink: 0,
    backgroundColor: theme.colors.DodgerBlue,
  },
  dotRead: { backgroundColor: "transparent" },
  itemBody: { minWidth: 0, flex: 1 },
  subject: {
    color: theme.colors.Violet,
    fontSize: "14px",
    marginBottom: "2px",
  },
  message: {
    color: theme.colors.Violet,
    opacity: 0.7,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "12.5px",
    marginBottom: "4px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  time: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "11px",
  },
  state: {
    padding: "36px 16px",
    textAlign: "center",
    color: theme.colors.Violet,
    opacity: 0.7,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13.5px",
  },
  loading: { display: "flex", justifyContent: "center", padding: "30px" },
}));

// Map a notification's opaque `context` to an in-app route, or null (no nav).
// The backend supplies routing hints; extend this as routes are added.
const routeForNotification = (n) => {
  const ctx = n?.context || {};
  if (typeof ctx.route === "string" && ctx.route.startsWith("/")) return ctx.route;
  if (typeof ctx.url === "string" && ctx.url.startsWith("/")) return ctx.url;
  return null;
};

function NotificationBell() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);

  const me = useSelector((s) => s.loginReducer?.me);
  const currentUser = useMemo(() => {
    if (me) return me;
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (e) {
      return null;
    }
  }, [me]);
  const canView = hasCap(currentUser, "notifications.view");

  const count = useSelector((s) => s.dashboardReducer?.notificationCount) || {
    unread: 0,
    total: 0,
  };
  const list = useSelector((s) => s.dashboardReducer?.notificationList);
  const loading = useSelector((s) => !!s.dashboardReducer?.notificationListLoading);

  const items = useMemo(() => {
    if (Array.isArray(list?.results)) return list.results;
    if (Array.isArray(list?.data)) return list.data;
    return [];
  }, [list]);

  const unread = Number(count?.unread) || 0;

  // Poll the badge count while mounted (only if the user can see notifications).
  useEffect(() => {
    if (!canView) return undefined;
    dispatch(getNotificationCountAction());
    const id = setInterval(() => dispatch(getNotificationCountAction()), COUNT_POLL_MS);
    return () => clearInterval(id);
  }, [canView, dispatch]);

  // There's another page while the envelope reports a `next` link/URL.
  const hasMore = !!list?.next;

  const open = (e) => {
    setAnchorEl(e.currentTarget);
    // Refresh the feed (page 1, replace) + count each time the panel opens.
    setPage(1);
    dispatch(getNotificationsAction({ page: 1, page_size: NOTIF_PAGE_SIZE }, false));
    dispatch(getNotificationCountAction());
  };
  const close = () => setAnchorEl(null);

  // Infinite scroll: when the panel is scrolled near the bottom and there's a
  // next page, fetch and append it.
  const handleScroll = (e) => {
    if (loading || !hasMore) return;
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (nearBottom) {
      const nextPage = page + 1;
      setPage(nextPage);
      dispatch(
        getNotificationsAction({ page: nextPage, page_size: NOTIF_PAGE_SIZE }, true)
      );
    }
  };

  const onTap = (n) => {
    if (!n?.is_read) dispatch(markNotificationReadAction(n.id));
    const path = routeForNotification(n);
    if (path) {
      close();
      history.push(path);
    }
  };

  if (!canView) {
    // Admin / HR have no notifications endpoint yet — show a disabled bell
    // (visible for layout parity, but not clickable). Everyone else: hide it.
    const isAdminOrHr = !!currentUser?.is_admin || !!currentUser?.is_hr;
    if (isAdminOrHr) {
      return (
        <IconButton aria-label="Notifications" disabled size="medium">
          <NotificationsNoneIcon style={{ color: theme.colors.Violet, opacity: 0.35 }} />
        </IconButton>
      );
    }
    return null;
  }

  return (
    <>
      <IconButton aria-label="Notifications" onClick={open} size="medium">
        <Badge
          badgeContent={unread}
          max={99}
          invisible={unread === 0}
          color="error"
        >
          <NotificationsNoneIcon style={{ color: theme.colors.Violet }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={close}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ style: { padding: 0 } }}
      >
        <div className={classes.panel}>
          <div className={classes.header}>
            <span className={classes.title}>Notifications</span>
            <button
              type="button"
              className={`${classes.markAll} ${unread === 0 ? classes.markAllDisabled : ""}`}
              disabled={unread === 0}
              onClick={() => dispatch(markAllNotificationsReadAction())}
            >
              Mark all as read
            </button>
          </div>

          <div className={classes.list} onScroll={handleScroll}>
            {loading && items.length === 0 ? (
              <div className={classes.loading}>
                <ThreeDots color={theme.colors.DodgerBlue} height={40} width={40} />
              </div>
            ) : items.length === 0 ? (
              <div className={classes.state}>You're all caught up.</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`${classes.item} ${!n.is_read ? classes.itemUnread : ""}`}
                  onClick={() => onTap(n)}
                >
                  <span className={`${classes.dot} ${n.is_read ? classes.dotRead : ""}`} />
                  <div className={classes.itemBody}>
                    <div
                      className={classes.subject}
                      style={{
                        fontFamily: n.is_read
                          ? theme.fontFamily.MARKPROMEDIUM
                          : theme.fontFamily.MARKPROBOLD,
                      }}
                    >
                      {n.subject || "Notification"}
                    </div>
                    {n.body ? <div className={classes.message}>{n.body}</div> : null}
                    <div className={classes.time}>
                      {n.created_at ? moment(n.created_at).fromNow() : ""}
                    </div>
                  </div>
                </div>
              ))
            )}
            {/* Bottom loader while the next page is being appended. */}
            {loading && items.length > 0 ? (
              <div className={classes.loading} style={{ padding: "16px" }}>
                <ThreeDots color={theme.colors.DodgerBlue} height={26} width={26} />
              </div>
            ) : null}
          </div>
        </div>
      </Menu>
    </>
  );
}

export default NotificationBell;
